import { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { calculateTotalPay } from '../../../utils/proposalCalculations';

// Use unified date utils
import { moment } from '../../../utils/dateUtils';

import {
  Box,
  Table,
  TablePagination,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  Typography,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';

// Icons
import CancelIcon from '@mui/icons-material/Cancel';
import CircleIcon from '@mui/icons-material/Circle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

// Hooks
import usePagination from '../../../hooks/usePagination';

// Styles
import { table_container_base } from '../../../static/styles';

const link_styles = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: '100%',
};

// Status config
const statusConfig = {
  'Active': { color: '#4caf50', bgColor: '#e8f5e9', icon: CircleIcon, label: 'Active' },
  'Pending': { color: '#ff9800', bgColor: '#fff3e0', icon: CircleIcon, label: 'Pending' },
  'Cold': { color: '#42a5f5', bgColor: '#e3f2fd', icon: CircleIcon, label: 'Cold' },
  'Completed': { color: '#2196f3', bgColor: '#e3f2fd', icon: CheckCircleIcon, label: 'Completed' },
  'Cancelled': { color: '#f44336', bgColor: '#ffebee', icon: CancelIcon, label: 'Cancelled' },
  'Lost': { color: '#e91e63', bgColor: '#fce4ec', icon: CircleIcon, label: 'Lost' },
};

// Mobile Proposal Card
const MobileProposalCard = ({ proposal }) => {
  const navigate = useNavigate();
  const status = statusConfig[proposal.status] || statusConfig['Active'];
  const StatusIcon = status.icon;
  const totalPay = calculateTotalPay(proposal);

  return (
    <Box
      onClick={() => navigate(`/proposals/view/${proposal.id}`)}
      sx={{
        p: 2,
        borderBottom: '1px solid #eee',
        cursor: 'pointer',
        '&:active': { backgroundColor: '#f5f5f5' },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body1" sx={{ fontWeight: 700, color: '#1976d2' }}>
          {proposal.publicId}
        </Typography>
        <Chip
          icon={<StatusIcon sx={{ fontSize: 12, color: status.color + ' !important' }} />}
          label={status.label}
          size="small"
          sx={{
            height: 22,
            backgroundColor: status.bgColor,
            color: status.color,
            fontWeight: 600,
            fontSize: 10,
            '& .MuiChip-icon': { color: status.color },
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AttachMoneyIcon sx={{ fontSize: 20, color: '#4caf50', mr: 0.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>
            {totalPay.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
          </Typography>
          {proposal.additionalNotes?.payment?.completed && (
            <Chip
              icon={<AttachMoneyIcon sx={{ fontSize: 14, color: '#4caf50 !important' }} />}
              label="Paid"
              size="small"
              sx={{ ml: 1, height: 20, backgroundColor: '#e8f5e9', color: '#4caf50', fontWeight: 600, fontSize: 10 }}
            />
          )}
        </Box>
        <ChevronRightIcon sx={{ color: '#ccc' }} />
      </Box>
    </Box>
  );
};

const AppointmentProposalsTable = ({ proposals }) => {
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePagination();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // ===================== MOBILE VIEW =====================
  if (isMobile) {
    return (
      <Box>
        {proposals?.length ? (
          <>
            {proposals.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((proposal) => (
              <MobileProposalCard key={proposal.id} proposal={proposal} />
            ))}
            {proposals.length > 5 && (
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={proposals?.length || 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{ borderTop: '1px solid #eee' }}
              />
            )}
          </>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#999' }}>
              No proposals
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  // ===================== DESKTOP VIEW (unchanged) =====================
  return (
    <Fragment>
      <TableContainer component={Paper} sx={table_container_base}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width="25%">Status</TableCell>
              <TableCell width="25%">Public ID</TableCell>
              <TableCell width="25%">Total To Pay</TableCell>
              <TableCell width="25%" align="center">Paid</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {proposals?.length ? (
              proposals.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((proposal) => (
                <TableRow key={proposal.id}>
                  <TableCell scope="row">
                    <Link href={`/proposals/view/${proposal.id}`} sx={{ ...link_styles, justifyContent: 'center' }} underline="none" color="rgba(0, 0, 0, 0.87)">
                      {proposal.status === 'Active' && (
                        <Fragment><CircleIcon sx={{ color: 'green', mr: 1, width: 15, height: 15 }} /> Active</Fragment>
                      )}
                      {proposal.status === 'Pending' && (
                        <Fragment><CircleIcon sx={{ color: 'orange', mr: 1, width: 15, height: 15 }} /> Pending</Fragment>
                      )}
                      {proposal.status === 'Cold' && (
                        <Fragment><CircleIcon sx={{ color: '#42a5f5', mr: 1, width: 15, height: 15 }} /> Cold</Fragment>
                      )}
                      {proposal.status === 'Completed' && (
                        <Fragment><CheckCircleIcon sx={{ color: 'green', mr: 1, width: 15, height: 15 }} /> Completed</Fragment>
                      )}
                      {proposal.status === 'Cancelled' && (
                        <Fragment><CancelIcon sx={{ color: 'red', mr: 1, width: 15, height: 15 }} /> Cancelled</Fragment>
                      )}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link href={`/proposals/view/${proposal.id}`} color="rgba(0, 0, 0, 0.87)" underline="none" sx={link_styles}>
                      {proposal.publicId}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link href={`/proposals/view/${proposal.id}`} color="rgba(0, 0, 0, 0.87)" underline="none" sx={link_styles}>
                      {calculateTotalPay(proposal).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                    </Link>
                  </TableCell>
                  <TableCell align="center">
                    <Link href={`/proposals/view/${proposal.id}`} color="rgba(0, 0, 0, 0.87)" underline="none" sx={{ ...link_styles, justifyContent: 'center' }}>
                      <AttachMoneyIcon 
                        sx={{ 
                          color: proposal.additionalNotes?.payment?.completed ? '#4caf50' : '#e0e0e0',
                          fontSize: 24
                        }} 
                      />
                    </Link>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} sx={{ textAlign: 'center' }}>
                  <strong>No Data</strong>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={proposals?.length || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Items per page"
      />
    </Fragment>
  );
};

export default AppointmentProposalsTable;
